import React, { type FC, memo, useCallback, useMemo, useState, useTransition, lazy, Suspense } from "react";
import { Button } from "@heroui/react";
import { FormattedMessage } from "react-intl";

import { type AssistantStep } from "@dms/components/DmsSortAssistant/buildAssistantQueue";
import { BankPickPanel } from "@dms/components/DmsSortAssistant/panels/BankPickPanel";
import { BewirtungPanel } from "@dms/components/DmsSortAssistant/panels/BewirtungPanel";
import { LaterButton, type Run } from "@dms/components/DmsSortAssistant/panels/panelShared";
import { WherePanel } from "@dms/components/DmsSortAssistant/panels/WherePanel";
import { useAssistantActions } from "@dms/components/DmsSortAssistant/useAssistantActions";
import { DmsUtils } from "@dms/scripts/utils/DmsUtils";
import { useDmsDocumentsMap } from "@domain/context/DmsDataContexts";
import { DocumentThumbnail } from "@ui-components/DmsAttachDocument/DocumentThumbnail";

// Loaded on demand so pdf.js stays out of screens that never open the preview.
const AssistantPdf = lazy(() =>
    import("@dms/components/DmsSortAssistant/AssistantPdf").then((m) => ({ default: m.AssistantPdf })),
);

type Props = {
    steps: AssistantStep[];
    onDone(answered: number, later: number): void;
};

const SortAssistantInlineImpl: FC<Props> = ({ steps: initialSteps, onDone }) => {
    const [steps, setSteps] = useState(initialSteps);
    const [index, setIndex] = useState(0);
    const [counts, setCounts] = useState({ answered: 0, later: 0 });
    const [isPreviewOpen, setPreviewOpen] = useState(false);
    const [isPending, startTransition] = useTransition();
    const { saveBewirtung, moveTo, linkAndMoveToBank } = useAssistantActions();
    const documentsMap = useDmsDocumentsMap();

    const step = steps[index];
    const document = step ? documentsMap.get(step.documentId) : undefined;
    const isLast = index + 1 >= steps.length;
    const progressValues = useMemo(() => ({ current: index + 1, total: steps.length }), [index, steps.length]);

    const advance = useCallback(
        (field: "answered" | "later") => {
            const next = { ...counts, [field]: counts[field] + 1 };
            setCounts(next);
            setPreviewOpen(false);
            if (isLast) {
                onDone(next.answered, next.later);
            } else {
                setIndex(index + 1);
            }
        },
        [counts, index, isLast, onDone],
    );

    const handleAnswered = useCallback(() => startTransition(() => advance("answered")), [advance]);
    const handleLater = useCallback(() => startTransition(() => advance("later")), [advance]);

    const run = useCallback<Run>(
        (action) =>
            startTransition(async () => {
                if (await action()) {
                    startTransition(() => advance("answered"));
                }
            }),
        [advance],
    );

    const togglePreview = useCallback(() => setPreviewOpen((open) => !open), []);

    const handleNoneOfThem = useCallback(
        () =>
            startTransition(() =>
                setSteps((prev) => prev.map((s, i) => (i === index ? { kind: "where", documentId: s.documentId } : s))),
            ),
        [index],
    );

    if (!step) return null;

    return (
        <div className="flex flex-col gap-3" data-testid="sort-assistant-inline">
            <div className="flex items-center gap-3 text-sm text-muted">
                {document && <DocumentThumbnail fileId={document.key} onClick={togglePreview} />}
                <div className="flex min-w-0 grow flex-col items-start gap-1">
                    <span className="max-w-full truncate">{document ? DmsUtils.getDownloadName(document) : ""}</span>
                    {document && (
                        <Button size="sm" variant="tertiary" onPress={togglePreview}>
                            <FormattedMessage
                                id={
                                    isPreviewOpen
                                        ? "app.dms.sortAssistant.hideDocument"
                                        : "app.dms.sortAssistant.showDocument"
                                }
                            />
                        </Button>
                    )}
                </div>
                <span className="shrink-0">
                    <FormattedMessage id="app.dms.sortAssistant.progress" values={progressValues} />
                </span>
            </div>
            {document && isPreviewOpen && (
                <div className="h-[420px] overflow-hidden rounded-lg border border-default">
                    <Suspense fallback={null}>
                        <AssistantPdf document={document} />
                    </Suspense>
                </div>
            )}
            {step.kind === "bewirtung" && !document && (
                <div className="flex flex-col gap-3">
                    <p role="alert" className="text-sm text-warning">
                        <FormattedMessage id="app.agent.sortQuestions.documentMissing" />
                    </p>
                    <LaterButton isPending={isPending} onLater={handleLater} />
                </div>
            )}
            {step.kind === "bewirtung" && document && (
                <BewirtungPanel
                    key={`${index}-bewirtung`}
                    document={document}
                    isPending={isPending}
                    run={run}
                    onAdvance={handleAnswered}
                    onLater={handleLater}
                    saveBewirtung={saveBewirtung}
                />
            )}
            {step.kind === "bankPick" && (
                <BankPickPanel
                    key={`${index}-bankPick`}
                    documentId={step.documentId}
                    companyId={document?.companyId}
                    candidateTransactionIds={step.candidateTransactionIds}
                    isPending={isPending}
                    run={run}
                    onNoneOfThem={handleNoneOfThem}
                    onLater={handleLater}
                    linkAndMoveToBank={linkAndMoveToBank}
                />
            )}
            {step.kind === "where" && (
                <WherePanel
                    key={`${index}-where`}
                    documentId={step.documentId}
                    isPending={isPending}
                    run={run}
                    onAdvance={handleAnswered}
                    onLater={handleLater}
                    moveTo={moveTo}
                />
            )}
        </div>
    );
};

export const SortAssistantInline = memo(SortAssistantInlineImpl);
